import AlertModal from 'components/shared/modal/Alert';
import ConfirmationModal from 'components/shared/modal/Confirmation';
import Modal from 'components/shared/modal/Modal';
import { uniqueId } from 'lodash';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const ModalContext = createContext({});

export const ModalProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const [registered, setRegistered] = useState({});
  const promiseRef = useRef({});
  const isProgrammaticClose = useRef(false);

  /*
   ------------------------------------------------
   Open Modal
   ------------------------------------------------
  */
  const openModal = useCallback((component, props = {}, options = {}) => {
    setDialog({
      id: uniqueId('modal_'),
      Component: component,
      props,
      ...options,
    });
  }, []);

  /*
   ------------------------------------------------
   Close Modal (manual close button)
   ------------------------------------------------
  */
  const closeModal = useCallback(() => {
    setDialog(null);
  }, [dialog]);

  /*
   ------------------------------------------------
   Handle Browser Back
   ------------------------------------------------
  */
  useEffect(() => {
    const handlePopState = () => {
      if (!dialog) return;

      // Close modal on back
      setDialog(null);
      isProgrammaticClose.current = false;
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dialog]);

  /*
   ------------------------------------------------
   Alert / Confirm
   ------------------------------------------------
  */
  const alert = (component = AlertModal, props = {}, options = {}) => {
    openModal(component, props, options);
    return new Promise((resolve, reject) => {
      promiseRef.current = { resolve, reject };
    });
  };

  const confirm = (component = ConfirmationModal, props = {}) => {
    openModal(component, {
      ...props,
      onConfirm: () => promiseRef.current.resolve(),
    });
    return new Promise((resolve, reject) => {
      promiseRef.current = { resolve, reject };
    });
  };

  /*
   ------------------------------------------------
   Named modals
   ------------------------------------------------
  */
  const registerNamedModal = useCallback((name, component, props = {}, options = {}) => {
    setRegistered((prev) => ({
      ...prev,
      [name]: { component, props, options },
    }));
  }, []);

  const openNamedModal = useCallback(
    (name, props = {}) => {
      if (!registered[name]) {
        throw new Error(`No such modal name: ${name}`);
      }

      const { component, props: registeredProps, options } = registered[name];

      openModal(component, { ...registeredProps, ...props }, options);
    },
    [registered, openModal]
  );

  return (
    <ModalContext.Provider
      value={{
        openModal,
        closeModal,
        alert,
        confirm,
        registerNamedModal,
        openNamedModal,
      }}
    >
      {children}

      {dialog && <Modal key={dialog.id} {...dialog} modal={{ closeModal }} />}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined || context === null) {
    throw new Error(`useModal must be called within ModalProvider`);
  }

  return context;
};

export const useModalRouter = (modalApi) => {
  // stores { routeName: renderFn }
  const routesRef = useRef({});

  // register a route
  const register = useCallback((name, renderFn) => {
    routesRef.current[name] = renderFn;
  }, []);

  // open a modal by route name
  const open = useCallback(
    (name, ctx = {}, options = {}) => {
      const route = routesRef.current[name];
      if (!route) throw new Error(`Unknown modal route: ${name}`);

      modalApi.openModal(
        (props = {}) => {
          return route(ctx, props);
        },
        {},
        options
      );
    },
    [modalApi]
  );

  // close active modal
  const close = useCallback(() => {
    modalApi.closeModal();
  }, [modalApi]);

  return {
    register,
    open,
    close,
  };
};
